"use client";
import React, { ReactNode, useEffect, useRef, useState } from "react";
import { View, ViewStyle } from "react-native";

type IntersectionObserverProps = {
  // Required props
  children:
    | ReactNode
    | ((state: {
        isIntersecting: boolean;
        hasTriggered: boolean;
      }) => ReactNode);

  // Callback functions
  onIntersect?: (
    isVisible: boolean,
    intersectionRatio?: number,
    entry?: IntersectionObserverEntry
  ) => void;
  onExit?: (
    isVisible: boolean,
    intersectionRatio?: number,
    entry?: IntersectionObserverEntry
  ) => void;

  // Intersection Observer options
  threshold?: number | number[];
  rootMargin?: string;
  root?: Element | null;

  // Component behavior
  triggerOnce?: boolean;
  partialExecution?: boolean;

  // React Native View props
  style?: ViewStyle;
  testID?: string;
  accessibilityLabel?: string;
};

export default function VisibilitySensor({
  children,
  onIntersect,
  onExit,
  threshold = 0.1,
  rootMargin = "0px",
  triggerOnce = false,
  partialExecution = false,
  root = null,
  style,
  ...otherProps
}: IntersectionObserverProps) {
  const targetRef = useRef<View>(null);
  const [hasTriggered, setHasTriggered] = useState<boolean>(false);
  const [isIntersecting, setIsIntersecting] = useState<boolean>(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    // Check if we're in a web environment and Intersection Observer is available
    if (typeof window === "undefined" || !window.IntersectionObserver) {
      // Fallback for non-web environments or unsupported browsers
      if (onIntersect && !triggerOnce) {
        onIntersect(true);
      }
      return;
    }

    const target = targetRef.current;
    if (!target) return;

    // If triggerOnce is true and we've already triggered, don't create a new observer
    if (triggerOnce && hasTriggered) return;

    const observerOptions: IntersectionObserverInit = {
      root,
      rootMargin,
      threshold: partialExecution ? [0, threshold].flat() : threshold,
    };

    const handleIntersection = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry: IntersectionObserverEntry) => {
        const isCurrentlyIntersecting: boolean = entry.isIntersecting;

        // Update intersection state
        setIsIntersecting(isCurrentlyIntersecting);

        if (isCurrentlyIntersecting) {
          // Element is intersecting
          if (!hasTriggered || !triggerOnce) {
            if (partialExecution) {
              // For partial execution, trigger based on intersection ratio
              const intersectionRatio: number = entry.intersectionRatio;
              if (onIntersect) {
                onIntersect(true, intersectionRatio, entry);
              }
            } else {
              // Standard execution - trigger when threshold is met
              if (onIntersect) {
                onIntersect(true, entry.intersectionRatio, entry);
              }
            }

            if (triggerOnce) {
              setHasTriggered(true);
              // Disconnect observer if triggerOnce is true
              if (observerRef.current) {
                observerRef.current.disconnect();
              }
            }
          }
        } else {
          // Element is not intersecting
          if (onExit && (!triggerOnce || !hasTriggered)) {
            onExit(false, entry.intersectionRatio, entry);
          }
        }
      });
    };

    // Create and start observing
    observerRef.current = new IntersectionObserver(
      handleIntersection,
      observerOptions
    );
    observerRef.current.observe(target as unknown as Element);

    // Cleanup function
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [
    onIntersect,
    onExit,
    threshold,
    rootMargin,
    triggerOnce,
    partialExecution,
    root,
    hasTriggered,
  ]);

  return (
    <View ref={targetRef} style={style} {...otherProps}>
      {typeof children === "function"
        ? children({ isIntersecting, hasTriggered })
        : children}
    </View>
  );
}

// Example usage:
/*
import IntersectionObserverComponent from './IntersectionObserverComponent';

// Basic usage
<IntersectionObserverComponent
  onIntersect={(isVisible, ratio, entry) => {
    console.log('Element is visible:', isVisible);
    console.log('Intersection ratio:', ratio);
  }}
  onExit={(isVisible, ratio, entry) => {
    console.log('Element exited viewport');
  }}
  threshold={0.5}
  triggerOnce={true}
  partialExecution={false}
>
  <Text>Content to observe</Text>
</IntersectionObserverComponent>

// With partial execution (triggers on any intersection amount)
<IntersectionObserverComponent
  onIntersect={(isVisible, ratio) => {
    console.log('Partial intersection ratio:', ratio);
  }}
  partialExecution={true}
  triggerOnce={false}
>
  <Text>Partially observable content</Text>
</IntersectionObserverComponent>

// With render prop pattern
<IntersectionObserverComponent
  onIntersect={() => console.log('Intersected!')}
  triggerOnce={true}
>
  {({ isIntersecting, hasTriggered }) => (
    <View style={{
      backgroundColor: isIntersecting ? 'green' : 'red',
      padding: 20
    }}>
      <Text>
        Status: {isIntersecting ? 'Visible' : 'Hidden'}
        {hasTriggered && ' (Triggered)'}
      </Text>
    </View>
  )}
</IntersectionObserverComponent>
*/
